
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { ElementWrapper } from '@cloudscape-design/test-utils-core/selectors';
import { appendSelector } from '@cloudscape-design/test-utils-core/utils';

export { ElementWrapper };

import AvatarWrapper from './avatar';
import ChatBubbleWrapper from './chat-bubble';
import LoadingBarWrapper from './loading-bar';
import SupportPromptGroupWrapper from './support-prompt-group';


export { AvatarWrapper };
export { ChatBubbleWrapper };
export { LoadingBarWrapper };
export { SupportPromptGroupWrapper };

declare module '@cloudscape-design/test-utils-core/dist/selectors' {
   interface ElementWrapper {
    
/**
 * Returns a wrapper that matches the Avatars with the specified CSS selector.
 * If no CSS selector is specified, returns a wrapper that matches Avatars.
 *
 * @param {string} [selector] CSS Selector
 * @returns {AvatarWrapper}
 */
findAvatar(selector?: string): AvatarWrapper;

/**
 * Returns a multi-element wrapper that matches Avatars with the specified CSS selector.
 * If no CSS selector is specified, returns a multi-element wrapper that matches Avatars.
 *
 * @param {string} [selector] CSS Selector
 * @returns {MultiElementWrapper<AvatarWrapper>}
 */
findAllAvatars(selector?: string): MultiElementWrapper<AvatarWrapper>;
/**
 * Returns a wrapper that matches the ChatBubbles with the specified CSS selector.
 * If no CSS selector is specified, returns a wrapper that matches ChatBubbles.
 *
 * @param {string} [selector] CSS Selector
 * @returns {ChatBubbleWrapper}
 */
findChatBubble(selector?: string): ChatBubbleWrapper;

/**
 * Returns a multi-element wrapper that matches ChatBubbles with the specified CSS selector.
 * If no CSS selector is specified, returns a multi-element wrapper that matches ChatBubbles.
 *
 * @param {string} [selector] CSS Selector
 * @returns {MultiElementWrapper<ChatBubbleWrapper>}
 */
findAllChatBubbles(selector?: string): MultiElementWrapper<ChatBubbleWrapper>;
/**
 * Returns a wrapper that matches the LoadingBars with the specified CSS selector.
 * If no CSS selector is specified, returns a wrapper that matches LoadingBars.
 *
 * @param {string} [selector] CSS Selector
 * @returns {LoadingBarWrapper}
 */
findLoadingBar(selector?: string): LoadingBarWrapper;

/**
 * Returns a multi-element wrapper that matches LoadingBars with the specified CSS selector.
 * If no CSS selector is specified, returns a multi-element wrapper that matches LoadingBars.
 *
 * @param {string} [selector] CSS Selector
 * @returns {MultiElementWrapper<LoadingBarWrapper>}
 */
findAllLoadingBars(selector?: string): MultiElementWrapper<LoadingBarWrapper>;
/**
 * Returns a wrapper that matches the SupportPromptGroups with the specified CSS selector.
 * If no CSS selector is specified, returns a wrapper that matches SupportPromptGroups.
 *
 * @param {string} [selector] CSS Selector
 * @returns {SupportPromptGroupWrapper}
 */
findSupportPromptGroup(selector?: string): SupportPromptGroupWrapper;

/**
 * Returns a multi-element wrapper that matches SupportPromptGroups with the specified CSS selector.
 * If no CSS selector is specified, returns a multi-element wrapper that matches SupportPromptGroups.
 *
 * @param {string} [selector] CSS Selector
 * @returns {MultiElementWrapper<SupportPromptGroupWrapper>}
 */
findAllSupportPromptGroups(selector?: string): MultiElementWrapper<SupportPromptGroupWrapper>;
   }
}


ElementWrapper.prototype.findAvatar = function(selector) {
  let rootSelector = `.${AvatarWrapper.rootSelector}`;
  if("legacyRootSelector" in AvatarWrapper && AvatarWrapper.legacyRootSelector){
    rootSelector = `:is(.${AvatarWrapper.rootSelector}, .${AvatarWrapper.legacyRootSelector})`;
  }
  // casting to 'any' is needed to avoid this issue with generics
  // https://github.com/microsoft/TypeScript/issues/29132
  return (this as any).findComponent(selector ? appendSelector(selector, rootSelector) : rootSelector, AvatarWrapper);
};

ElementWrapper.prototype.findAllAvatars = function(selector) {
  return this.findAllComponents(AvatarWrapper, selector);
};
ElementWrapper.prototype.findChatBubble = function(selector) {
  let rootSelector = `.${ChatBubbleWrapper.rootSelector}`;
  if("legacyRootSelector" in ChatBubbleWrapper && ChatBubbleWrapper.legacyRootSelector){
    rootSelector = `:is(.${ChatBubbleWrapper.rootSelector}, .${ChatBubbleWrapper.legacyRootSelector})`;
  }
  // casting to 'any' is needed to avoid this issue with generics
  // https://github.com/microsoft/TypeScript/issues/29132
  return (this as any).findComponent(selector ? appendSelector(selector, rootSelector) : rootSelector, ChatBubbleWrapper);
};

ElementWrapper.prototype.findAllChatBubbles = function(selector) {
  return this.findAllComponents(ChatBubbleWrapper, selector);
};
ElementWrapper.prototype.findLoadingBar = function(selector) {
  let rootSelector = `.${LoadingBarWrapper.rootSelector}`;
  if("legacyRootSelector" in LoadingBarWrapper && LoadingBarWrapper.legacyRootSelector){
    rootSelector = `:is(.${LoadingBarWrapper.rootSelector}, .${LoadingBarWrapper.legacyRootSelector})`;
  }
  // casting to 'any' is needed to avoid this issue with generics
  // https://github.com/microsoft/TypeScript/issues/29132
  return (this as any).findComponent(selector ? appendSelector(selector, rootSelector) : rootSelector, LoadingBarWrapper);
};

ElementWrapper.prototype.findAllLoadingBars = function(selector) {
  return this.findAllComponents(LoadingBarWrapper, selector);
};
ElementWrapper.prototype.findSupportPromptGroup = function(selector) {
  let rootSelector = `.${SupportPromptGroupWrapper.rootSelector}`;
  if("legacyRootSelector" in SupportPromptGroupWrapper && SupportPromptGroupWrapper.legacyRootSelector){
    rootSelector = `:is(.${SupportPromptGroupWrapper.rootSelector}, .${SupportPromptGroupWrapper.legacyRootSelector})`;
  }
  // casting to 'any' is needed to avoid this issue with generics
  // https://github.com/microsoft/TypeScript/issues/29132
  return (this as any).findComponent(selector ? appendSelector(selector, rootSelector) : rootSelector, SupportPromptGroupWrapper);
};

ElementWrapper.prototype.findAllSupportPromptGroups = function(selector) {
  return this.findAllComponents(SupportPromptGroupWrapper, selector);
};


export default function wrapper(root: string = 'body') {
  return new ElementWrapper(root);
}
